/**
 * Module-scoped cache for Price Comparison responses.
 *
 * The cache lives for the app's lifetime (survives React route
 * unmount/remount), enabling stale-while-revalidate on the Comparison
 * tab: returning to the tab renders instantly from cache while a
 * background request revalidates. Entries are keyed by the exact query
 * params so each filter/sort combination is cached independently.
 *
 * `clearComparisonCache()` is called after a manual data refresh so the
 * next Comparison mount fetches fresh data instead of showing stale rows.
 */
const _cache = new Map();

/** Build a stable cache key independent of param insertion order. */
export function comparisonCacheKey(params) {
  const ordered = {};
  for (const k of Object.keys(params).sort()) ordered[k] = params[k];
  return JSON.stringify(ordered);
}

/** Return the cached response for a key, or null. */
export function getCachedComparison(key) {
  return _cache.get(key) ?? null;
}

/** Whether a key is present in the cache. */
export function hasCachedComparison(key) {
  return _cache.has(key);
}

/** Store a response under a key. */
export function setCachedComparison(key, data) {
  _cache.set(key, data);
}

/** Drop all cached comparison responses (e.g. after a data refresh). */
export function clearComparisonCache() {
  _cache.clear();
}
